import React from "react";
import { EwbDtls } from "../../models/invoice/invoice.model";
import { IOrder, IOrderAddress, IOrderGoods } from "../../models/Order.model";

type InvoiceSummaryProps = {
  order?: IOrder;
  invoiceNumber: string;
  goods: IOrderGoods[];
  isPartialInvoice: boolean;
  warehouseName?: string;
  billingAddress: IOrderAddress;
  shippingAddress: IOrderAddress;
  ewbDetails?: EwbDtls;
};

const formatCurrency = (amount: number) =>
  `₹${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatAddress = (address?: IOrderAddress) =>
  [
    address?.line1,
    address?.line2 || address?.address,
    address?.city,
    address?.state,
  ]
    .filter(Boolean)
    .concat(address?.pincode ? [`PIN ${address.pincode}`] : [])
    .join(", ") || "Not provided";

const InvoiceSummary: React.FC<InvoiceSummaryProps> = ({
  order,
  invoiceNumber,
  goods,
  isPartialInvoice,
  warehouseName,
  billingAddress,
  shippingAddress,
  ewbDetails,
}) => {
  const goodsRows = goods.map((item, index) => {
    const quantity = Number(item.quantity) || 0;
    const unitPrice = Number(item.price) || 0;
    const discount = Number(item.discount) || 0;
    const gstRate = Number(item.gst || item.product?.gst) || 0;
    const taxableAmount = unitPrice * quantity - discount;

    return {
      item,
      index,
      quantity,
      unitPrice,
      discount,
      gstRate,
      gstAmount: (taxableAmount * gstRate) / 100,
      total: taxableAmount * (1 + gstRate / 100),
    };
  });
  const goodsSubtotal = goodsRows.reduce(
    (sum, row) => sum + row.unitPrice * row.quantity,
    0,
  );
  const goodsDiscount = goodsRows.reduce((sum, row) => sum + row.discount, 0);
  const goodsTax = goodsRows.reduce((sum, row) => sum + row.gstAmount, 0);
  const invoiceTotal = isPartialInvoice
    ? goodsRows.reduce((sum, row) => sum + row.total, 0)
    : order?.totalAmount || 0;

  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
      <div className="space-y-6">
        <section className="grid grid-cols-1 gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2">
          <div>
            <p className="text-xs font-medium text-slate-500">Invoice number</p>
            <p className="mt-1 text-sm font-semibold text-slate-900">
              {invoiceNumber || "-"}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-slate-500">Order</p>
            <p className="mt-1 text-sm font-semibold text-slate-900">
              {order?.orderNumber || "-"}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-slate-500">Customer</p>
            <p className="mt-1 text-sm font-semibold text-slate-900">
              {order?.customerName || order?.customerId?.name || "-"}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-slate-500">GSTIN</p>
            <p className="mt-1 text-sm text-slate-800">
              {order?.gstin || "Not provided"}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-slate-500">Warehouse</p>
            <p className="mt-1 text-sm font-semibold text-slate-900">
              {warehouseName || "Not selected"}
            </p>
          </div>
        </section>

        <section>
          <div className="mb-3 flex items-baseline justify-between gap-3">
            <h3 className="text-sm font-semibold text-slate-900">
              Goods and prices
            </h3>
            <span className="text-xs text-slate-500">
              {goods.length} {goods.length === 1 ? "item" : "items"}
            </span>
          </div>
          <div className="space-y-3 xl:hidden">
            {goodsRows.length ? (
              goodsRows.map(
                ({
                  item,
                  index,
                  quantity,
                  unitPrice,
                  discount,
                  gstRate,
                  gstAmount,
                  total,
                }) => (
                  <article
                    key={
                      item._id ||
                      `${item.product?._id || item.rawMaterial?._id}-${index}`
                    }
                    className="rounded-lg border border-slate-200 p-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h4 className="break-words text-sm font-semibold text-slate-900">
                          {item.product?.productName ||
                            item.rawMaterial?.name ||
                            "Unnamed item"}
                        </h4>
                        <p className="mt-1 text-xs text-slate-500">
                          HSN{" "}
                          {item.hsn ||
                            item.product?.hsn ||
                            item.rawMaterial?.hsn ||
                            "-"}
                        </p>
                      </div>
                      <p className="shrink-0 text-sm font-semibold text-slate-900">
                        {formatCurrency(total)}
                      </p>
                    </div>
                    <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 border-t border-slate-100 pt-3 text-xs">
                      <div>
                        <dt className="text-slate-500">Quantity</dt>
                        <dd className="mt-0.5 font-medium text-slate-800">
                          {quantity} {item.unit || ""}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-slate-500">Unit price</dt>
                        <dd className="mt-0.5 font-medium text-slate-800">
                          {formatCurrency(unitPrice)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-slate-500">Discount</dt>
                        <dd className="mt-0.5 font-medium text-slate-800">
                          {formatCurrency(discount)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-slate-500">GST ({gstRate}%)</dt>
                        <dd className="mt-0.5 font-medium text-slate-800">
                          {formatCurrency(gstAmount)}
                        </dd>
                      </div>
                    </dl>
                  </article>
                ),
              )
            ) : (
              <p className="rounded-lg border border-slate-200 px-3 py-6 text-center text-sm text-slate-500">
                No goods are listed on this order.
              </p>
            )}
          </div>
          <div className="hidden overflow-x-auto rounded-lg border border-slate-200 xl:block">
            <table className="w-full min-w-[680px] text-left text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500">
                <tr>
                  <th className="px-3 py-2.5 font-medium">Item</th>
                  <th className="px-3 py-2.5 text-right font-medium">Qty</th>
                  <th className="px-3 py-2.5 text-right font-medium">
                    Unit price
                  </th>
                  <th className="px-3 py-2.5 text-right font-medium">
                    Discount
                  </th>
                  <th className="px-3 py-2.5 text-right font-medium">GST</th>
                  <th className="px-3 py-2.5 text-right font-medium">
                    Line total
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {goods.length ? (
                  goodsRows.map(
                    ({
                      item,
                      index,
                      quantity,
                      unitPrice,
                      discount,
                      gstRate,
                      gstAmount,
                      total,
                    }) => (
                      <tr
                        key={
                          item._id ||
                          `${item.product?._id || item.rawMaterial?._id}-${index}`
                        }
                      >
                        <td className="px-3 py-3 font-medium text-slate-900">
                          {item.product?.productName ||
                            item.rawMaterial?.name ||
                            "Unnamed item"}
                          <span className="mt-0.5 block text-xs font-normal text-slate-500">
                            HSN{" "}
                            {item.hsn ||
                              item.product?.hsn ||
                              item.rawMaterial?.hsn ||
                              "-"}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-right text-slate-700">
                          {quantity} {item.unit || ""}
                        </td>
                        <td className="px-3 py-3 text-right text-slate-700">
                          {formatCurrency(unitPrice)}
                        </td>
                        <td className="px-3 py-3 text-right text-slate-700">
                          {formatCurrency(discount)}
                        </td>
                        <td className="px-3 py-3 text-right text-slate-700">
                          {gstRate}% ({formatCurrency(gstAmount)})
                        </td>
                        <td className="px-3 py-3 text-right font-semibold text-slate-900">
                          {formatCurrency(total)}
                        </td>
                      </tr>
                    ),
                  )
                ) : (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-3 py-6 text-center text-sm text-slate-500"
                    >
                      No goods are listed on this order.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="ml-auto mt-3 w-full max-w-xs space-y-1.5 text-sm">
            <div className="flex justify-between text-slate-600">
              <span>Goods subtotal</span>
              <span>{formatCurrency(goodsSubtotal)}</span>
            </div>
            <div className="flex justify-between text-slate-600">
              <span>Item discounts</span>
              <span>-{formatCurrency(goodsDiscount)}</span>
            </div>
            <div className="flex justify-between text-slate-600">
              <span>GST</span>
              <span>{formatCurrency(goodsTax)}</span>
            </div>
            {!isPartialInvoice && !!order?.discountPrice && (
              <div className="flex justify-between text-slate-600">
                <span>Order discount</span>
                <span>-{formatCurrency(order.discountPrice)}</span>
              </div>
            )}
            {!isPartialInvoice && !!order?.transportPrice && (
              <div className="flex justify-between text-slate-600">
                <span>Transport</span>
                <span>{formatCurrency(order.transportPrice)}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-slate-200 pt-2 font-semibold text-slate-900">
              <span>Invoice total</span>
              <span>{formatCurrency(invoiceTotal)}</span>
            </div>
          </div>
        </section>

        <section className="space-y-4 border-t border-slate-200 pt-5">
          <div>
            <h3 className="text-xs font-semibold uppercase text-slate-500">
              Billing address
            </h3>
            <p className="mt-1 text-sm text-slate-800">
              {formatAddress(billingAddress)}
            </p>
          </div>
          <div>
            <h3 className="text-xs font-semibold uppercase text-slate-500">
              Shipping address
            </h3>
            <p className="mt-1 text-sm text-slate-800">
              {formatAddress(shippingAddress)}
            </p>
          </div>
        </section>

        <section className="border-t border-slate-200 pt-5">
          <h3 className="mb-3 text-sm font-semibold text-slate-900">
            E-way bill details
          </h3>
          {ewbDetails ? (
            <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-xs text-slate-500">Transporter</dt>
                <dd className="mt-0.5 text-slate-800">
                  {ewbDetails.TransId || ewbDetails.TransName}
                </dd>
              </div>
              {/* <div>
                <dt className="text-xs text-slate-500">Distance</dt>
                <dd className="mt-0.5 text-slate-800">
                  {ewbDetails.Distance} km
                </dd>
              </div> */}
              <div>
                <dt className="text-xs text-slate-500">
                  Transport document date
                </dt>
                <dd className="mt-0.5 text-slate-800">
                  {ewbDetails.TransDocDt}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">Vehicle</dt>
                <dd className="mt-0.5 text-slate-800">
                  {ewbDetails.VehNo} ·{" "}
                  {ewbDetails.VehType === "R"
                    ? "Regular"
                    : "Over dimensional cargo"}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">Transport mode</dt>
                <dd className="mt-0.5 text-slate-800">
                  {
                    (
                      {
                        "1": "Road",
                        "2": "Rail",
                        "3": "Air",
                        "4": "Ship",
                      } as Record<string, string>
                    )[ewbDetails.TransMode]
                  }
                </dd>
              </div>
            </dl>
          ) : (
            <p className="text-sm text-slate-600">
              No EWB details will be submitted for this order.
            </p>
          )}
        </section>
      </div>
    </div>
  );
};

export default InvoiceSummary;
